/**
 * telemetryService.js — PRD-151 WP2 first-party product telemetry (MVP).
 *
 * Fire-and-forget event queue that batches to the backend
 * `POST /api/telemetry-track` ({ events: [...] }). The backend (requireAuth)
 * derives userId + tier from the CIAM idToken; the client only ships the
 * event shape { eventType, route, sessionId, ts, meta }.
 *
 * PRIVACY INVARIANT (do not violate):
 *   - `route` is ALWAYS the normalized, query/hash-stripped path with dynamic
 *     id segments templated (see usePageView). Never a raw ?q=acme.com URL.
 *   - `meta.feature` / `meta.action` are short fixed ENUMS only — NEVER a
 *     domain, asset, IP, search string, or email. `detail` is a bounded enum
 *     (disposition/plan/config-kind), never free user input.
 *
 * AUTH / FLUSH PATH (why fetch, not sendBeacon, is primary):
 *   `navigator.sendBeacon` cannot set an Authorization header, and the server
 *   ignores any body-carried auth (requireAuth reads the Bearer header only).
 *   So the PRIMARY flush uses `fetch(..., { keepalive: true })` with an
 *   `Authorization: Bearer <idToken>` header — keepalive lets a small POST
 *   survive navigation/unload, which is all the `pagehide` flush needs (it
 *   exists only to not lose the tail of the queue, not to be a separate
 *   transport). We keep the most-recent idToken cached in-module so the
 *   pagehide flush can attach the header without an async token acquire during
 *   unload. `sendBeacon` is a last-resort fallback ONLY when no cached token is
 *   available (unauthenticated beacons the server would reject anyway, so this
 *   is effectively a no-op safety net and never carries auth).
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE_URL = 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';
const TELEMETRY_ENDPOINT = `${API_BASE_URL}/telemetry-track`;

const isDev = process.env.NODE_ENV === 'development';

// react-snap pre-render guard — MUST mirror the exact detection in index.js so
// telemetry is fully inert while react-snap captures static HTML.
const isPrerendering =
  typeof navigator !== 'undefined' &&
  (navigator.userAgent.includes('HeadlessChrome') ||
    navigator.userAgent.includes('ReactSnap'));

const FLUSH_INTERVAL_MS = 10000; // periodic flush
const FLUSH_AT_COUNT = 20; // flush when the queue reaches this many events

// ── Auth helpers (mirror scanService.js exactly — idToken via MSAL) ──────────
// Kept local rather than imported so telemetry has no coupling to scanService's
// scan-specific surface; the token-acquisition contract is identical.
async function getAuthToken() {
  try {
    const msalInstance = window.msalInstance;
    if (!msalInstance) return null;
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return null;
    const response = await msalInstance.acquireTokenSilent({
      scopes: ['openid', 'profile', 'email'],
      account: accounts[0],
    });
    return response.idToken || null;
  } catch (error) {
    return null;
  }
}

function hasAccount() {
  try {
    const msalInstance = window.msalInstance;
    if (!msalInstance) return false;
    return msalInstance.getAllAccounts().length > 0;
  } catch {
    return false;
  }
}

// ── Session id (per-tab) ─────────────────────────────────────────────────────
// CAVEAT: sessionStorage is per-tab, so a user with N tabs open produces N
// distinct sessionIds — session counts are inflated relative to true user
// sessions. Acceptable for MVP funnel analysis; revisit if we need true
// cross-tab sessions.
const SESSION_KEY = 'da_telemetry_session_id';
function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // sessionStorage unavailable (privacy mode) — fall back to a volatile id.
    return `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

// ── In-memory queue + state ──────────────────────────────────────────────────
let queue = [];
let flushTimer = null;
let cachedToken = null; // most-recent idToken, used by the pagehide flush
let listenersBound = false;

function ensureTimer() {
  if (flushTimer || isPrerendering) return;
  flushTimer = setInterval(() => {
    flush();
  }, FLUSH_INTERVAL_MS);
}

function bindUnloadListeners() {
  if (listenersBound || isPrerendering || typeof window === 'undefined') return;
  listenersBound = true;
  // pagehide fires on tab close / bfcache navigation more reliably than
  // 'unload'. We flush the tail synchronously via keepalive fetch.
  window.addEventListener('pagehide', () => flush({ unload: true }));
  // visibilitychange → hidden is a belt-and-suspenders backup for mobile.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush({ unload: true });
  });
}

/**
 * Send a batch of events. Primary transport is keepalive fetch with a Bearer
 * header (so requireAuth passes). During unload we use the cached token to
 * avoid an async acquire; sendBeacon is only a last-resort fallback.
 */
async function sendEvents(events, { unload } = {}) {
  if (!events.length) return;
  const body = JSON.stringify({ events });

  // Acquire a token. On unload we prefer the cached token (no async wait);
  // otherwise refresh it and cache for the next unload.
  let token = cachedToken;
  if (!unload) {
    const fresh = await getAuthToken();
    if (fresh) {
      token = fresh;
      cachedToken = fresh;
    }
  }

  if (token) {
    try {
      await fetch(TELEMETRY_ENDPOINT, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body,
      });
      return;
    } catch {
      // fall through to beacon last-resort below
    }
  }

  // Last-resort fallback: sendBeacon cannot carry auth, so the server will
  // reject it (requireAuth). This path only runs when we truly have no token
  // (effectively a no-op safety net) — it exists so a flush never throws.
  if (navigator.sendBeacon) {
    try {
      navigator.sendBeacon(TELEMETRY_ENDPOINT, new Blob([body], { type: 'application/json' }));
    } catch {
      /* swallow — telemetry is best-effort */
    }
  }
}

/**
 * Flush the current queue. Safe to call anytime; no-ops when empty, when
 * unauthenticated, or during pre-render.
 */
function flush(opts = {}) {
  if (isPrerendering) return;
  if (!queue.length) return;
  if (!hasAccount()) {
    // Unauthenticated: never emit. Drop the queue so it can't leak later.
    queue = [];
    return;
  }
  const batch = queue;
  queue = [];
  // Fire and forget — never await in caller paths (page-view, unload).
  sendEvents(batch, opts);
}

/**
 * track(eventType, { route, meta }) — enqueue one telemetry event.
 * No-ops when unauthenticated or during react-snap pre-render.
 *
 * @param {string} eventType  e.g. 'page_view' | 'login' | 'action'
 * @param {{ route?: string, meta?: object }} [opts]
 */
export function track(eventType, { route, meta } = {}) {
  if (isPrerendering) return;
  if (!hasAccount()) return; // unauthenticated → no-op

  const event = {
    eventType,
    route: route || null,
    sessionId: getSessionId(),
    ts: new Date().toISOString(),
    meta: meta || null,
  };

  queue.push(event);

  // Dev-console mirror (matches onboardingAnalytics style).
  if (isDev) {
    console.log(`[Telemetry] ${eventType}`, { route: event.route, meta: event.meta });
  }

  ensureTimer();
  bindUnloadListeners();

  // Warm the cached token in the background so the pagehide flush has one.
  if (!cachedToken) {
    getAuthToken().then((t) => {
      if (t) cachedToken = t;
    });
  }

  if (queue.length >= FLUSH_AT_COUNT) {
    flush();
  }
}

/**
 * Normalize a raw pathname into a low-cardinality, PII-free route template.
 * Strips query + hash (caller passes location.pathname, but we defensively
 * strip again) and maps concrete dynamic id segments to placeholders.
 */
export function normalizeRoute(pathname) {
  if (!pathname) return '/';
  // Defensive: strip any query/hash that slipped in.
  let path = pathname.split('?')[0].split('#')[0];

  const rules = [
    [/^\/assets\/[^/]+$/, '/assets/:assetId'],
    [/^\/scan-result\/[^/]+$/, '/scan-result/:scanId'],
    [/^\/scrutinize-result\/[^/]+$/, '/scrutinize-result/:scanId'],
    [/^\/validate\/[^/]+$/, '/validate/:jobId'],
  ];
  for (const [re, template] of rules) {
    if (re.test(path)) return template;
  }
  return path;
}

/**
 * usePageView() — fire a page_view on every route change.
 * Emits BOTH the first-party track('page_view', { route }) and the GA4
 * gtag page_view (one hook serves both). Both are guarded against
 * unauth/pre-render by track()'s no-op and the gtag presence check.
 */
export function usePageView() {
  const location = useLocation();

  useEffect(() => {
    const route = normalizeRoute(location.pathname);

    // First-party (no-ops if unauth / pre-render).
    track('page_view', { route });

    // GA4 — send the normalized (PII-free) path, never the raw URL.
    if (!isPrerendering && typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', { page_path: route });
    }
  }, [location.pathname]);
}

const telemetryService = { track, usePageView, normalizeRoute };
export default telemetryService;
